<div class="page">
  <app-page-header [title]="'rankings.title' | translate" [subtitle]="'rankings.description' | translate" />

  <p class="accent-surface mb-8 rounded-2xl border px-5 py-4 text-sm">
    {{ 'rankings.participationOrder' | translate }}
  </p>

  <div class="mb-8 grid gap-4 lg:grid-cols-[auto_1fr]">
    <div class="card flex w-fit flex-wrap items-center gap-2 rounded-2xl p-2" role="group" [attr.aria-label]="'accessibility.rankingPeriod' | translate">
      <label class="px-2 text-[10px] font-black uppercase tracking-widest text-slate-400">{{ 'common.season' | translate }}</label>
      <app-styled-select
        [options]="seasonOptions()"
        [value]="selectedSeasonStart().toString()"
        [ariaLabel]="'common.season' | translate"
        density="compact"
        (valueChange)="setSeasonStart($event)"
      />
      <button type="button" (click)="setRankingScope('all')"
        [class.accent-solid]="rankingScope() === 'all'" [class.text-white]="rankingScope() === 'all'"
        class="rounded-xl px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 transition-colors">
        {{ 'rankings.allTime' | translate }}
      </button>
    </div>

    <div class="card flex flex-wrap items-center gap-2 rounded-2xl p-2" role="group" [attr.aria-label]="'accessibility.rankingDiscipline' | translate">
      <button type="button" (click)="setDiscipline(null)"
        [class.accent-solid]="!disciplineFilter()" [class.text-white]="!disciplineFilter()"
        class="rounded-xl px-3 py-2 text-xs font-black text-slate-500 dark:text-slate-400 transition-colors">
        {{ 'rankings.allDisciplines' | translate }}
      </button>
      @for (disc of availableDisciplines(); track disc) {
      <button type="button" (click)="setDiscipline(disc)"
        [class.accent-solid]="disciplineFilter() === disc" [class.text-white]="disciplineFilter() === disc"
        class="rounded-xl px-3 py-2 text-xs font-black text-slate-500 dark:text-slate-400 transition-colors">
        {{ formatDiscipline(disc) }}
      </button>
      }
    </div>
  </div>

  <div class="card mb-8 flex flex-wrap items-end gap-6 p-6">
    <!-- Search Bar (Restored) -->
    <div class="relative min-w-0 flex-1 basis-72">
      <div class="absolute inset-y-0 left-4 flex items-center pointer-events-none">
        <svg class="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input type="search" [value]="searchTerm()" (input)="onSearch($event)" [placeholder]="'rankings.searchPlaceholder' | translate"
        [attr.aria-label]="'rankings.searchPlaceholder' | translate" class="field pl-12">
    </div>

    <div class="flex flex-wrap items-center gap-4">
      <!-- Gender Filter -->
      <div>
        <label for="rankings-gender" class="field-label">{{ 'rankings.gender' | translate }}</label>
        <select id="rankings-gender" (change)="setGender($any($event.target).value)" class="field min-w-[150px]">
          <option value="">{{ 'rankings.allGenders' | translate }}</option>
          <option value="Male" [selected]="genderFilter() === 'Male'">{{ 'rankings.male' | translate }}</option>
          <option value="Female" [selected]="genderFilter() === 'Female'">{{ 'rankings.female' | translate }}</option>
        </select>
      </div>

      <!-- Region Filter -->
      <div>
        <label for="rankings-region" class="field-label">{{ 'admin.importRanking.form.region' | translate }}</label>
        <select id="rankings-region" disabled class="field min-w-[150px]">
          <option value="HAUTS-DE-FRANCE" selected>Hauts-de-France</option>
        </select>
      </div>

      <!-- Category Filter -->
      <div>
        <label for="rankings-category" class="field-label">{{ 'rankings.categoryLabel' | translate }}</label>
        <select id="rankings-category" (change)="setCategory($any($event.target).value)" class="field min-w-[150px]">
          <option value="">{{ 'rankings.allCategories' | translate }}</option>
          <option value="Junior" [selected]="categoryFilter() === 'Junior'">{{ 'rankings.junior' | translate }}</option>
          <option value="Senior" [selected]="categoryFilter() === 'Senior'">{{ 'rankings.senior' | translate }}</option>
          <option value="V1" [selected]="categoryFilter() === 'V1'">{{ 'rankings.veteran1' | translate }}</option>
          <option value="V2" [selected]="categoryFilter() === 'V2'">{{ 'rankings.veteran2' | translate }}</option>
          <option value="V3" [selected]="categoryFilter() === 'V3'">{{ 'rankings.veteran3' | translate }}</option>
        </select>
      </div>

      <!-- Reset -->
      @if (regionFilter() || disciplineFilter() || categoryFilter() || genderFilter() || searchTerm() || clubFilter() !== 'ALL') {
        <button type="button" (click)="clearFilters()" class="back-link px-2 py-3">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          {{ 'common.reset' | translate }}
        </button>
      }
    </div>
  </div>

  @if (clubFilter() !== 'ALL') {
  <div
    class="accent-surface mb-6 flex items-center justify-between rounded-2xl border p-4">
    <div class="flex items-center gap-3">
      <div class="accent-solid rounded-lg p-2">
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      </div>
      <div>
        <p class="text-xs font-bold uppercase tracking-widest">{{
          'rankings.filtersTitle' | translate }}</p>
        <p class="text-lg font-black text-slate-900 dark:text-slate-100">
          {{ clubFilter() === '_INDEPENDENT_' ? ('clubsKiosk.independents' | translate) : clubFilter() }}
        </p>
      </div>
    </div>
    <button (click)="clubFilter.set('ALL')"
      class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
  }

  @if (isLoading()) {
    <app-branded-loader [label]="'common.loading' | translate" />
  } @else {
  <div class="grid grid-cols-1 lg:grid-cols-6 2xl:grid-cols-8 gap-6 items-start">
    <!-- Left Skyscraper -->
    <aside class="hidden lg:block lg:col-span-1 sticky top-8 space-y-6">
      <div class="flex flex-col gap-6">
        <!-- Summary Skyscraper -->
        <div
          class="accent-gradient rounded-3xl p-6 flex flex-col items-center text-center min-h-[400px]">
          <div class="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-6">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 class="text-[10px] font-black uppercase tracking-[0.3em] mb-4 opacity-70">{{
            'rankings.sidebar.participants' | translate }}</h3>
          <p class="text-6xl font-black mb-auto">{{ stats().totalParticipants }}</p>
          <div class="w-full mt-6 pt-6 border-t border-white/10">
            <span class="text-[10px] font-black uppercase tracking-widest opacity-60 block mb-2">{{
              'raceDetail.stats.resultsCount' | translate }}</span>
            <span class="text-2xl font-black">{{ rawResultCount() }}</span>
          </div>
        </div>

        <!-- Club Skyscraper -->
        <div
          class="card flex min-h-[400px] flex-col p-6">
          <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 text-center">{{
            'rankings.sidebar.topClubs' | translate }}</h3>
          <div class="flex-1 space-y-4">
            @for (club of stats().topClubs; track club.name; let i = $index) {
            <div
              class="group relative flex flex-col items-center p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 transition-all hover:scale-105">
              <span
                class="absolute -top-2 -left-2 w-6 h-6 rounded-lg accent-solid text-[10px] font-black flex items-center justify-center shadow-lg">#{{
                i + 1 }}</span>
              <span
                class="text-xs font-black text-slate-900 dark:text-slate-100 text-center line-clamp-2 mb-2 uppercase">{{
                club.name }}</span>
              <span class="text-xl font-black text-accent">{{ club.count }}</span>
            </div>
            }
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Rankings Table -->
    <div class="lg:col-span-4 2xl:col-span-6">
      <div
        class="card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-left">
            <thead>
              <tr class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest w-16">🏆</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{{ 'rankings.table.runner' | translate }}</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest w-10 text-center hidden md:table-cell">🏳️</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hidden sm:table-cell">{{ 'rankings.table.club' | translate }}</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">{{ 'rankings.medals' | translate }}</th>
                <th aria-sort="descending" class="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">{{ 'rankings.table.racesParticipated' | translate }} ↓</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">{{ 'rankings.table.totalDistance' | translate }}</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest text-center w-28 hidden md:table-cell">{{ 'rankings.table.totalTime' | translate }}</th>
                <th class="py-3 px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest text-center w-28">{{ 'compare.averageSpeed' | translate }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              @for (item of paginatedRankings(); track item.human.id; let i = $index) {
              <tr class="group hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                <td class="px-4 py-4 whitespace-nowrap text-xs font-black text-slate-300 dark:text-slate-600">{{
                  (currentPage() - 1) * itemsPerPage + i + 1 }}</td>
                <td class="px-4 py-4 whitespace-nowrap text-xs">
                  <a [routerLink]="['/human-profile', item.human.id]"
                    class="group-hover:text-[color:var(--accent-logo)] transition-colors font-black text-slate-900 dark:text-slate-100">
                    {{ item.human.name }} <app-registered-star [humanId]="item.human.id" />
                  </a>
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-xl text-center hidden md:table-cell">
                  @if (item.human.nationality) {
                    <span [title]="item.human.nationality">{{ getFlagEmoji(item.human.nationality) }}</span>
                  }
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-xs font-bold text-slate-500 dark:text-slate-400 hidden sm:table-cell">
                  {{ item.human.club | displayValue }}
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-xs text-slate-800 dark:text-slate-200 text-center font-black">
                  <div class="flex items-center justify-center gap-3">
                    <span [title]="'humanProfile.palmares.victories' | translate" [attr.aria-label]="('humanProfile.palmares.victories' | translate) + ': ' + item.medals.first">🥇 {{ item.medals.first }}</span>
                    <span [title]="'humanProfile.palmares.silver' | translate" [attr.aria-label]="('humanProfile.palmares.silver' | translate) + ': ' + item.medals.second">🥈 {{ item.medals.second }}</span>
                    <span [title]="'humanProfile.palmares.bronze' | translate" [attr.aria-label]="('humanProfile.palmares.bronze' | translate) + ': ' + item.medals.third">🥉 {{ item.medals.third }}</span>
                    <span [title]="'humanProfile.palmares.chocolate' | translate" [attr.aria-label]="('humanProfile.palmares.chocolate' | translate) + ': ' + item.medals.fourth">🍫 {{ item.medals.fourth }}</span>
                  </div>
                </td>
                <td
                  class="px-4 py-4 whitespace-nowrap text-xs text-slate-800 dark:text-slate-200 text-center font-black">
                  {{ item.raceCount }}
                </td>
                <td
                  class="px-4 py-4 whitespace-nowrap text-xs text-slate-800 dark:text-slate-200 text-center font-bold">
                  @if (item.totalDistanceKm > 0) {
                    {{ item.totalDistanceKm | distance }} KM
                  } @else {
                    {{ null | displayValue }}
                  }
                </td>
                <td
                  class="px-4 py-4 whitespace-nowrap text-xs text-slate-800 dark:text-slate-200 text-center font-mono hidden md:table-cell">
                  {{ formatTotalTime(item.totalTimeSeconds) }}
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-xs text-slate-800 dark:text-slate-200 text-center font-bold">
                  @if (item.totalTimeSeconds > 0 && item.totalDistanceKm > 0) {
                    {{ (item.totalDistanceKm * 3600 / item.totalTimeSeconds) | distance }} km/h
                  } @else {
                    {{ null | displayValue }}
                  }
                </td>
              </tr>
              }
 @empty {
              <tr>
                <td colspan="9" class="px-6 py-20 text-center text-slate-500 dark:text-slate-400">
                  <div class="flex flex-col items-center opacity-40">
                    <svg class="w-16 h-16 text-slate-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                        d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-slate-400 font-black uppercase text-sm tracking-widest">{{ 'rankings.noResults' |
                      translate }}</p>
                  </div>
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        @if (totalPages() > 1) {
        <div
          class="px-6 py-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button (click)="previousPage()" [disabled]="currentPage() === 1"
              class="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed hover:text-[color:var(--accent-logo)] transition-all shadow-sm">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span class="text-sm font-black text-slate-500 uppercase tracking-widest">
              {{ currentPage() }} / {{ totalPages() }}
            </span>
            <button (click)="nextPage()" [disabled]="currentPage() === totalPages()"
              class="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed hover:text-[color:var(--accent-logo)] transition-all shadow-sm">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        }
      </div>
    </div>

    <!-- Right Skyscraper -->
    <aside class="hidden lg:block lg:col-span-1 sticky top-8 space-y-6">
      <div class="flex flex-col gap-6">
        <!-- Breeds Skyscraper -->
        <div
          class="card flex min-h-[400px] flex-col p-6">
          <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 text-center">{{
            'rankings.sidebar.topBreeds' | translate }}</h3>
          <div class="flex-1 space-y-4">
            @for (breed of stats().topBreeds; track breed.name) {
            <div
              class="flex flex-col p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 transition-all hover:scale-105">
              <span class="text-xs font-black text-slate-900 dark:text-slate-300 uppercase leading-tight break-words mb-1" [title]="breed.name">{{ breed.name }}</span>
              <span class="text-2xl font-black text-accent">{{ breed.count }}</span>
            </div>
            }
          </div>
        </div>

        <!-- Disciplines Skyscraper -->
        <div
          class="card flex min-h-[400px] flex-col p-6">
          <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 text-center">{{
            'rankings.sidebar.disciplines' | translate }}</h3>
          <div class="flex-1 space-y-6">
            @for (disc of stats().disciplineStats; track disc.name) {
            <div class="flex flex-col px-2">
              <div class="flex justify-between items-end mb-2">
                <span class="text-[9px] font-black text-slate-500 uppercase tracking-wider leading-tight break-words min-w-0 flex-1">{{ formatDiscipline(disc.name)
                  }}</span>
                <span class="text-xs font-black text-accent">{{ disc.count }}</span>
              </div>
              <div class="w-full h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div class="h-full bg-[var(--region-accent)] rounded-full"
                  [style.width.%]="rawResultCount() ? (disc.count / rawResultCount()) * 100 : 0"></div>
              </div>
            </div>
            }
          </div>
        </div>
      </div>
    </aside>
  </div>
  }
</div>
